import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import type { IReturn } from "@repo/shopify/api/fetchReturnOrder";
import { fetchReturnOrder } from "@repo/shopify/api/fetchReturnOrder";
import type { IRequestReturnResponse } from "@repo/shopify/api/requestReturn";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import { formatOrderAddress } from "@repo/shopify/utils/address";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";
import Price from "@atoms/Price";

import Address from "@molecules/Address";
import ReturnLineItem from "@molecules/ReturnLineItem";

import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IState extends IRequestReturnResponse {
  returnShippingFee: number;
}

export default function ReturnRequested() {
  const navigate = useNavigate();
  const location = useLocation();

  const { openToast, setMeta, showFooter } = useLayoutStore();

  const { t } = useTranslation();
  const { lang } = useLanguage();

  const { customer } = useCustomerStore();

  const {
    returnId = "",
    returnRefundLineItems = [],
    returnShippingFee = 0,
    refundDuties = [],
  } = location.state ? (location.state as IState) : {};

  const [isLoading, setIsLoading] = useState(true);
  const [returnOrder, setReturnOrder] = useState<IReturn | undefined>(undefined);

  useEffect(() => {
    setMeta({ title: `Return Requested - ${DEFAULT_TITLE}` });
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!returnId) navigate(URLS.NOT_FOUND);
    else void getReturnOrder();
  }, [returnId]);

  const getReturnOrder = async () => {
    if (returnId && returnRefundLineItems) {
      try {
        const response = await fetchReturnOrder({
          storeName: import.meta.env.VITE_REACT_APP_SHOP_NAME as string,
          domain: lang === "ko" ? "kr" : "intl",
          returnId,
          customerId: customer.id,
          returnRefundLineItems,
          refundDuties,
          refundShippingFee: returnShippingFee,
        });

        if (!response) {
          openToast(NOTIFICATIONS.ERROR_NO_RETURN_PRODUCT);
          navigate(-1);
        }

        const returnOrderData = response?.data.return;
        setReturnOrder(returnOrderData);
      } catch (error) {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 결제 수단 정보
  const paymentInfo = useOrderPayMethod({ lang, order: returnOrder?.order });

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: returnOrder?.order?.shippingAddress, lang });

  // 반품배송비가 상품의 가격을 초과 할 경우 (kr은 isAdditionalCharge가 발생하지 않는다고 가정)
  const isAdditionalCharge =
    lang === "en" ? Number(returnOrder?.suggestedRefund?.subtotal?.shopMoney.amount || "0") - returnShippingFee < 0 : false;

  const refundTotalTax = lang === "en" ? Number(returnOrder?.suggestedRefund?.totalTax?.shopMoney.amount || "0") : 0;
  const refundTotalDuties = lang === "en" ? Number(returnOrder?.suggestedRefund?.totalDuties?.shopMoney.amount || "0") : 0;

  return (
    <>
      {!returnOrder || isLoading ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-10 pc:grid-cols-subgrid pc:gap-y-5 col-span-8 grid grid-cols-subgrid gap-y-4">
          <h2 className="pc:text-[1.125rem] col-span-8 text-lg">Return Requested ({returnOrder?.order.name || ""})</h2>
          <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-10 leading-none">
            <div className="text-md pc:gap-2.5 flex flex-col gap-2">
              <p className="pc:leading-[145%] inline-block leading-[130%]">
                <Trans i18nKey="return_requested.description" />
              </p>
              <Link
                as="square"
                className="flex! items-center justify-center px-3!"
                href="/information#shipping-and-returns"
                variant="secondary"
              >
                {t("return_requested.button")}
              </Link>
            </div>
            <section className="flex flex-col gap-5">
              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span className="uppercase">Estimated Period</span>
                <span className="font-normal">{t("return_requested.estimated_period")}</span>
              </div>
              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span className="uppercase">Refund Method</span>
                <span className="font-normal">{paymentInfo}</span>
              </div>
              <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                <span className="uppercase">Pick-up Address</span>
                <Address {...shippingAddress} />
              </div>
            </section>
          </section>

          <section className="pc:pt-0 pc:col-span-5 pc:col-start-4 pc:grid-cols-subgrid pc:gap-y-10 col-span-8 grid grid-cols-subgrid gap-2.5 pt-6 leading-none">
            <div className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-2.5">
              <span className="pc:col-span-5 col-span-8">Line Items</span>
              <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-4">
                {returnOrder?.returnLineItems.edges.map(({ node }) => {
                  const originalTotalSet = parseFloat(node.fulfillmentLineItem.originalTotalSet.shopMoney.amount);
                  const calculatedDiscountAmount = node.fulfillmentLineItem.lineItem.discountAllocations
                    .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount))
                    .reduce((a, b) => a + b, 0);

                  // TODO: 추후 프론트단에서 계산하지말고 람다에서 계산된 값을 반환해주도록 수정할 예정
                  const discountedTotalSet = originalTotalSet - calculatedDiscountAmount;

                  return (
                    <ReturnLineItem
                      compareAtPrice={discountedTotalSet.toString()}
                      currentStep="requested"
                      customerNote={node.customerNote}
                      disabled={!node.fulfillmentLineItem.lineItem.variant}
                      id={node.id}
                      imageUrl={node.fulfillmentLineItem.lineItem.image?.url}
                      key={node.id}
                      originalQuantity={node.fulfillmentLineItem.quantity}
                      price={discountedTotalSet.toString()}
                      returnQuantity={node.quantity}
                      returnReason={node.returnReason}
                      size={node.fulfillmentLineItem.lineItem.variant.selectedOptions.find((option) => option.name === "Size")?.value || ""}
                      title={node.fulfillmentLineItem.lineItem.title}
                      variantTitle={node.fulfillmentLineItem.lineItem.variantTitle}
                    />
                  );
                })}
              </ul>
            </div>
            <div className="pc:col-span-2 pc:col-start-4 col-span-8 col-start-1 flex flex-col gap-y-1">
              {/* 해당 상품 결제 당시 금액*/}
              {/* 결제 금액 (subtotal) - 환불될 전체 금액, 상품 가격, 할인, 세금, 배송비 등 모든 요소가 포함 */}
              <div className="flex justify-between">
                <span>Subtotal</span>
                <Price price={Number(returnOrder?.suggestedRefund.discountedSubtotal.shopMoney.amount || "0")} />
              </div>
              {/* 반품 배송비 */}
              <div className="flex justify-between">
                <span>Estimated Shipping</span>
                <Price isNegative price={returnShippingFee === 0 ? 0 : returnShippingFee} />
              </div>
              {/* 환불될 Tax */}
              {refundTotalTax > 0 ? (
                <div className="flex justify-between">
                  <span>Taxes</span>
                  <Price price={refundTotalTax} />
                </div>
              ) : null}
              {/* 환불될 Duty */}
              {refundTotalDuties > 0 ? (
                <div className="flex justify-between">
                  <span>Duties</span>
                  <Price price={refundTotalDuties} />
                </div>
              ) : null}
              {/* 예상 환불 금액*/}
              {/* amount - 환불 대상이 되는 상품의 가격 합계, 상품 가격만 포함하며, 할인이나 세금, 배송비 등은 제외된 값 */}
              <div className="flex justify-between">
                <span>{isAdditionalCharge ? "Additional Fee" : "Estimated Refund"}</span>
                <Price
                  price={
                    isAdditionalCharge
                      ? Math.abs(Number(returnOrder?.suggestedRefund.discountedSubtotal.shopMoney.amount || "0") - returnShippingFee)
                      : Number(returnOrder?.suggestedRefund.amount.shopMoney.amount || "0")
                  }
                />
              </div>
            </div>
          </section>
        </section>
      )}
    </>
  );
}
